import { useCallback, useEffect, useState } from 'react'
import {
  AgxButton,
  AgxColumn,
  AgxDatePicker,
  AgxEmptyState,
  AgxRow,
} from '@urbanx/agx.ui-components2'
import { SubPagePanel } from 'components/panel/SubPagePanel'
import {
  WaiverOfCoolingOff,
  CoolingOffCondition,
} from '@urbanx/agx.campaigns.client'
import dayjs from 'dayjs'
import { useSelectedCampaign } from 'hooks'
import { useBusinessDate } from 'hooks/useBusinessDate'

const MAX_COOLING_OFF_DAYS = 5

interface WaiverOfCoolingOffPanelProps {
  contractDate?: Date
  waiverOfCoolingOff?: WaiverOfCoolingOff
  onChange: (_updatedWaiverOfCoolingOff: WaiverOfCoolingOff | undefined) => void
  onDelete: () => void
  hideEditButton?: boolean
  hideDeleteButton?: boolean
}

export function WaiverOfCoolingOffPanel({
  contractDate,
  waiverOfCoolingOff,
  onChange,
  onDelete,
  hideEditButton,
  hideDeleteButton,
}: WaiverOfCoolingOffPanelProps) {
  const { campaign } = useSelectedCampaign()
  const { moveToNextBusinessDay, addBusinessDays, isNonBusinessDay } =
    useBusinessDate(campaign?.state || '')
  const [editCoolingOffEndDate, setEditCoolingOffEndDate] = useState(false)

  const calculateCoolingOffEndDate = useCallback(() => {
    // If IncludeShorteningOfCoolingOff - shortenCoolingOffDays is the cooling off period length (in business days) after shortening.
    const shortenedCoolingOffDays = waiverOfCoolingOff?.shortenCoolingOffDays
    if (
      waiverOfCoolingOff?.coolingOffCondition ===
        CoolingOffCondition.IncludeShorteningOfCoolingOff &&
      shortenedCoolingOffDays != null
    ) {
      return addBusinessDays(
        dayjs(contractDate),
        shortenedCoolingOffDays
      ).toDate()
    }

    // IncludeWaiverOfCoolingOff - full waiver means no cooling off, due date is contract date
    if (
      waiverOfCoolingOff?.coolingOffCondition ===
      CoolingOffCondition.IncludeWaiverOfCoolingOff
    ) {
      return contractDate
    }

    // No condition - default full cooling off period (5 business days)
    return addBusinessDays(dayjs(contractDate), MAX_COOLING_OFF_DAYS).toDate()
  }, [waiverOfCoolingOff, contractDate, addBusinessDays])

  const [coolingOffEndDate, setCoolingOffEndDate] = useState<Date | undefined>(
    calculateCoolingOffEndDate()
  )

  useEffect(() => {
    setCoolingOffEndDate(calculateCoolingOffEndDate())
  }, [calculateCoolingOffEndDate])

  const countBusinessDaysBetween = useCallback(
    (startDate: Date, endDate: Date): number => {
      let count = 0
      let current = dayjs(startDate)
      const end = dayjs(endDate)

      while (current.isBefore(end, 'day')) {
        current = current.add(1, 'day')
        if (!isNonBusinessDay(current)) {
          count++
        }
      }
      return count
    },
    [isNonBusinessDay]
  )

  const saveCoolingOffEndDate = useCallback(() => {
    if (!editCoolingOffEndDate) {
      setEditCoolingOffEndDate(true)
      return
    }

    if (!coolingOffEndDate || !contractDate) return

    const businessDaysDiff = countBusinessDaysBetween(
      contractDate,
      coolingOffEndDate
    )

    if (businessDaysDiff === 0) {
      // Due date is contract date - full waiver of cooling off
      onChange({
        coolingOffCondition: CoolingOffCondition.IncludeWaiverOfCoolingOff,
      })
    } else if (
      businessDaysDiff > 0 &&
      businessDaysDiff < MAX_COOLING_OFF_DAYS
    ) {
      // Shortened cooling off period - store the cooling off period length
      onChange({
        coolingOffCondition: CoolingOffCondition.IncludeShorteningOfCoolingOff,
        shortenCoolingOffDays: businessDaysDiff,
      })
    } else {
      // 5 or more business days - full cooling off (no waiver needed)
      onChange({})
    }

    setEditCoolingOffEndDate(false)
  }, [
    coolingOffEndDate,
    contractDate,
    editCoolingOffEndDate,
    onChange,
    countBusinessDaysBetween,
  ])

  // Show "There is no Cooling Off Period" when waiver is included (buyer waived their rights)
  if (
    waiverOfCoolingOff?.coolingOffCondition ===
    CoolingOffCondition.IncludeWaiverOfCoolingOff
  ) {
    return (
      <SubPagePanel
        key="cooling-off-period-panel"
        expandable
        panelTitle="Cooling Off Period"
        defaultExpanded
      >
        <AgxEmptyState variant="minimal" label="There is no Cooling Off" />
        {!hideDeleteButton && (
          <AgxRow
            gap="spacing-md"
            fill
            padding={{ top: 'spacing-3xl' }}
            alignItems="flex-end"
          >
            <AgxColumn gap="spacing-md" fill></AgxColumn>
            <AgxButton
              id="cooling-off-period-delete"
              variant="naked"
              danger
              text="Delete"
              onClick={onDelete}
            />
          </AgxRow>
        )}
      </SubPagePanel>
    )
  }

  return (
    <SubPagePanel
      key="cooling-off-period-panel"
      expandable
      panelTitle="Cooling Off Period"
      defaultExpanded
    >
      <AgxRow gap="spacing-md" fill alignItems="flex-end">
        <>
          <AgxDatePicker
            id="cooling-off-period-due-date"
            stretch
            disabled={!editCoolingOffEndDate}
            label="Due Date"
            value={coolingOffEndDate}
            optionalText=""
            minDate={dayjs(contractDate).add(-1, 'day').toDate()}
            maxDate={addBusinessDays(
              dayjs(contractDate),
              MAX_COOLING_OFF_DAYS
            ).toDate()}
            onChange={(value) => {
              setCoolingOffEndDate(
                value ? moveToNextBusinessDay(dayjs(value)).toDate() : undefined
              )
            }}
          />
          {!hideEditButton && (
            <AgxButton
              id="cooling-off-period-edit-due-date"
              variant="hollow"
              text={editCoolingOffEndDate ? 'Save' : 'Edit'}
              onClick={saveCoolingOffEndDate}
            />
          )}
        </>
      </AgxRow>
      {!hideDeleteButton && (
        <AgxRow
          gap="spacing-md"
          fill
          padding={{ top: 'spacing-3xl' }}
          alignItems="flex-end"
          justifyContent="right"
        >
          <AgxColumn gap="spacing-md" fill></AgxColumn>
          <AgxButton
            id="cooling-off-period-delete"
            variant="link"
            danger
            text="Delete"
            onClick={onDelete}
          />
        </AgxRow>
      )}
    </SubPagePanel>
  )
}
